import { useState, useEffect } from 'react'
import { API_BASE } from '../../config/api'

interface ReviewCard {
  name: string
  role: string
  quote: string
  stars: number
}

interface StatCard {
  value: string
  label: string
  crosshair: string
}

interface TagCard {
  title: string
  type: 'tech' | 'lifecycle'
  isDotted?: boolean
}

export default function BentoStream() {
  const defaultReviews: ReviewCard[] = [
    { name: 'Sarah Khan', role: 'CEO, Nova', quote: 'Attention to detail exceeded all expectations.', stars: 5 },
    { name: 'David Mwangi', role: 'CTO, FinEdge', quote: 'World-class backend engineering and SLA.', stars: 5 },
    { name: 'Aisha Raza', role: 'Founder, MediCare+', quote: 'Beautifully designed and performant app.', stars: 5 },
    { name: 'James Lee', role: 'PM, Atlas', quote: 'Professional, responsive, and delivered on time.', stars: 5 },
  ]

  const [liveReviews, setLiveReviews] = useState<ReviewCard[]>(defaultReviews)

  useEffect(() => {
    async function fetchReviews() {
      try {
        const res = await fetch(`${API_BASE}/api/v1/reviews`)
        if (!res.ok) throw new Error('API failed')
        const json = await res.json()
        if (json.success && Array.isArray(json.data) && json.data.length > 0) {
          const mapped: ReviewCard[] = json.data.map((r: any) => ({
            name: r.authorName,
            role: r.authorRole,
            quote: r.quote,
            stars: r.rating
          }))
          setLiveReviews(mapped)
        }
      } catch (err) {
        console.error('Failed to fetch reviews for BentoStream:', err)
      }
    }
    fetchReviews()
  }, [])

  const stats: StatCard[] = [
    { value: '50+', label: 'Digital platforms shipped', crosshair: '⌖' },
    { value: '99.9%', label: 'Production server SLA uptime', crosshair: '⊙' },
    { value: '25+', label: 'Enterprise & startup clients', crosshair: '⎋' },
    { value: '100%', label: 'Client project retention', crosshair: '✦' },
  ]

  const tags: TagCard[] = [
    { title: '01. DISCOVER', type: 'lifecycle' },
    { title: 'React / Vite', type: 'tech', isDotted: true },
    { title: '02. DESIGN', type: 'lifecycle' },
    { title: 'Flutter / Dart', type: 'tech' },
    { title: '03. BUILD', type: 'lifecycle' },
    { title: 'Node.js / Express', type: 'tech', isDotted: true },
    { title: '04. LAUNCH', type: 'lifecycle' },
    { title: 'AWS / Cloudflare', type: 'tech' },
  ]

  // Repeat items to ensure they span far taller than any screen height
  const reviewsRepeatCount = Math.max(4, Math.ceil(12 / Math.max(1, liveReviews.length)))
  const safeReviewsRepeat = reviewsRepeatCount % 2 === 0 ? reviewsRepeatCount : reviewsRepeatCount + 1
  const reviewsLoop = Array.from({ length: safeReviewsRepeat }).flatMap(() => liveReviews)

  const statsRepeatCount = Math.max(4, Math.ceil(12 / Math.max(1, stats.length)))
  const safeStatsRepeat = statsRepeatCount % 2 === 0 ? statsRepeatCount : statsRepeatCount + 1
  const statsLoop = Array.from({ length: safeStatsRepeat }).flatMap(() => stats)

  const tagsRepeatCount = Math.max(4, Math.ceil(12 / Math.max(1, tags.length)))
  const safeTagsRepeat = tagsRepeatCount % 2 === 0 ? tagsRepeatCount : tagsRepeatCount + 1
  const tagsLoop = Array.from({ length: safeTagsRepeat }).flatMap(() => tags)

  const reviewsDuration = 70 * (safeReviewsRepeat / 2)
  const statsDuration = 50 * (safeStatsRepeat / 2)
  const tagsDuration = 80 * (safeTagsRepeat / 2)

  return (
    <div className="bento-stream-container blueprint-dots">
      {/* Dynamic drifting background ambient glow */}
      <div className="ambient-glow" />

      {/* Column 1: Reviews (Scrolls UP - Slow) */}
      <div className="stream-column scroll-up-slow" style={{ zIndex: 2, animationDuration: `${reviewsDuration}s` }}>
        {reviewsLoop.map((r, i) => (
          <div 
            key={`rev-${i}`} 
            className={`stream-card relative overflow-hidden ${i % 2 === 0 ? 'rotate-left' : 'rotate-right'}`}
          >
            {/* Editorial quote watermark */}
            <span className="quote-bg-glyph">“</span>
            
            <div className="text-[0.68rem] tracking-wider mb-1 select-none" style={{ color: 'var(--accent-green)' }}>
              {'★'.repeat(r.stars)}
            </div>
            
            <p className="text-[0.72rem] leading-normal font-medium mb-2 relative" style={{ color: 'var(--text)', zIndex: 2 }}>
              "{r.quote}"
            </p>
            
            <div className="relative" style={{ zIndex: 2 }}>
              <div className="font-display text-[0.68rem] font-bold" style={{ color: 'var(--text)' }}>
                {r.name}
              </div>
              <div className="text-[0.62rem]" style={{ color: 'var(--muted)' }}>
                {r.role}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Column 2: Stats (Scrolls DOWN - Medium) */}
      <div className="stream-column scroll-down-medium" style={{ zIndex: 2, animationDuration: `${statsDuration}s` }}>
        {statsLoop.map((s, i) => (
          <div 
            key={`stat-${i}`} 
            className={`stream-card text-center py-4 relative ${i % 2 === 0 ? 'rotate-right' : 'rotate-left'}`}
          >
            {/* Technical crosshair indicator */}
            <span className="absolute top-2 right-2 text-[0.55rem]" style={{ color: 'var(--border-strong)' }}>
              {s.crosshair}
            </span>
            
            <div className="font-display text-lg md:text-xl font-bold mb-1" style={{ color: 'var(--text)' }}>
              {s.value}
            </div>
            <div className="text-[0.62rem] leading-tight" style={{ color: 'var(--muted)' }}>
              {s.label}
            </div>
          </div>
        ))}
      </div>

      {/* Column 3: Tech & Lifecycle (Scrolls UP - Fast) */}
      <div className="stream-column scroll-up-fast" style={{ zIndex: 2, animationDuration: `${tagsDuration}s` }}>
        {tagsLoop.map((t, i) => (
          <div 
            key={`tag-${i}`} 
            className={`stream-card text-center relative ${i % 2 === 0 ? 'rotate-left' : 'rotate-right'} ${t.isDotted ? 'stream-card-dotted' : ''}`}
            style={{
              background: t.type === 'lifecycle' ? 'var(--surface-2)' : 'var(--surface)',
              borderColor: t.type === 'lifecycle' ? 'var(--text)' : 'var(--border)'
            }}
          >
            {/* Micro blueprint mark */}
            {t.type === 'lifecycle' && (
              <span className="absolute top-1 left-1.5 text-[0.45rem]" style={{ color: 'var(--accent-green)' }}>●</span>
            )}
            
            <div 
              className="font-display text-[0.68rem] font-bold" 
              style={{ color: 'var(--text)' }}
            >
              {t.title}
            </div>
            <div className="text-[0.58rem] mt-0.5" style={{ color: 'var(--muted)' }}>
              {t.type === 'lifecycle' ? 'Lifecycle' : 'Tech Stack'}
            </div>
          </div>
        ))}
      </div>

    </div>
  )
}
